/**
 * Veersa Points — BRD / L&D framework constants.
 */

/** Five framework activities (Participation, Certifications, Facilitation, Mentoring, Content Creation). */
export const VEERSA_POINTS_FRAMEWORK_ACTIVITY_CODES = new Set([
  "FACILITATING",
  "PARTICIPATION",
  "CERTIFICATION",
  "MENTORING",
  "CONTENT_CREATION",
]);

/** Self-service "Log a new activity" — Certifications, Mentoring, Content Creation only. */
export const VEERSA_POINTS_SELF_LOG_ACTIVITY_CODES = new Set([
  "CERTIFICATION",
  "MENTORING",
  "CONTENT_CREATION",
]);

export const VEERSA_POINTS_DRILLDOWN_EXCLUDED_ACTIVITY_CODES = new Set([
  "TEAM_PARTICIPATION",
]);

export const VEERSA_POINTS_FACILITATING_ACTIVITY_CODE = "FACILITATING";

/** Training and Session sub-tabs (not standalone sidebar modules). */
export const VEERSA_POINTS_TAB = "VEERSA POINTS";
export const POINTS_DASHBOARD_TAB = "POINTS DASHBOARD";

export const VEERSA_POINTS_EVIDENCE_HINTS = {
  CERTIFICATION: "Upload certificate or completion proof (required).",
  MENTORING: "Upload mentee details and outcome summary (required).",
  CONTENT_CREATION: "Upload final content or provide a link below (at least one required).",
};

export const badgeStyles = {
  green: { background: "#EAF3DE", color: "#3B6D11" },
  amber: { background: "#FAEEDA", color: "#854F0B" },
  blue: { background: "#E6F1FB", color: "#185FA5" },
};

export const badgeLabels = {
  green: (a) => `${a.occurrences} / ${a.maxOccurrences} done`,
  amber: (a) => `${a.occurrences} / ${a.maxOccurrences} ${a.unit}`,
  blue: (a) => `${a.occurrences} / ${a.maxOccurrences} ${a.unit}`,
};

/** Admin Points dashboard — chart buckets for the five framework activities. */
export const VEERSA_POINTS_FRAMEWORK_CHART_DEF = [
  { label: "Participation", color: "#7F77DD" },
  { label: "Certifications", color: "#378ADD" },
  { label: "Facilitation", color: "#888780" },
  { label: "Mentoring", color: "#1D9E75" },
  { label: "Content Creation", color: "#D85A30" },
];

/** Map submission display names (incl. legacy) to a framework chart bucket, or null to skip. */
export function bucketVeersaPointsActivityChartLabel(displayName, activityCode) {
  const code = String(activityCode || "").toUpperCase();
  if (code === "PARTICIPATION" || code === "TEAM_PARTICIPATION") return "Participation";
  if (code === "CERTIFICATION") return "Certifications";
  if (code === "FACILITATING") return "Facilitation";
  if (code === "MENTORING") return "Mentoring";
  if (code === "CONTENT_CREATION" || code === "BLOG_WRITING") return "Content Creation";

  const s = (displayName || "").toLowerCase().trim();
  if (!s) return null;
  if (s.includes("facilit")) return "Facilitation";
  if (s.includes("mentor")) return "Mentoring";
  if (s.includes("certif")) return "Certifications";
  if (s.includes("content") && (s.includes("creat") || s.includes("creation"))) return "Content Creation";
  if (s.includes("blog")) return "Content Creation";
  if (s.includes("team") && s.includes("particip")) return "Participation";
  if (s.includes("particip") || s.includes("training") || s.includes("program")) return "Participation";
  if (s.includes("refer")) return null;

  const exact = VEERSA_POINTS_FRAMEWORK_CHART_DEF.find((d) => d.label.toLowerCase() === s);
  return exact ? exact.label : null;
}

/** Build horizontal bar chart rows from admin submission grid data. */
export function activityChartRowsFromVeersaPointsSubmissions(submissionRows) {
  const counts = Object.fromEntries(VEERSA_POINTS_FRAMEWORK_CHART_DEF.map((d) => [d.label, 0]));
  (submissionRows || []).forEach((r) => {
    const dn = (r.displayName || "").trim();
    const exact = VEERSA_POINTS_FRAMEWORK_CHART_DEF.find((d) => d.label.toLowerCase() === dn.toLowerCase());
    const label = exact
      ? exact.label
      : bucketVeersaPointsActivityChartLabel(dn, r.activityCode || r.ActivityCode);
    if (!label) return;
    counts[label] = (counts[label] || 0) + 1;
  });
  return VEERSA_POINTS_FRAMEWORK_CHART_DEF.map((d) => ({
    label: d.label,
    count: counts[d.label] || 0,
    color: d.color,
  }));
}
